/**
 * web/src/screens/Notebook/hooks/context.ts — the context-panel / view seam
 * of useNotebookState (card vncVrGk1hc9i, round two): which card is open in
 * the desktop context panel, the feed's roving tab stop, the viewport hinge
 * and the remembered List/Grid pick. Pure move out of the former hooks.ts.
 */
import { useCallback, useState } from 'react';
import { session } from '../../../shell/bridge';

export function useNotebookContext() {
  /* ── Desktop uplift phase 2 (card 2wn9seit8zGi, PRD §6.1 + §6.4) ──────────
     The context panel is the third zone of the desktop shell. `contextId` is
     the ONE card open in it — the PRD is explicit that only one item is open
     at a time (P1), and that Esc closes it. Below 760px there is no third
     zone at all (PRD L5: the existing mobile layout applies), so the panel
     simply does not render and these state writes cost nothing. */
  const [contextId, setContextId] = useState<string | null>(null);

  /* PRD §8 — which card holds the feed's single tab stop (roving tabindex):
     Tab reaches the feed once, Arrow keys walk it. */
  const [feedRoving, setFeedRoving] = useState<string | null>(null);

  /* PRD L1/L5 hinge on the viewport: >=1100px the panel is a sticky third
     column, 760-1100px it is a right slide-over, <760px it does not exist.
     Read once, then kept current — a resize across the hinge has to move the
     panel, not leave a fixed drawer stranded on a wide canvas. */
  const [viewportW, setViewportW] = useState<number>(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth);

  const contextLive = viewportW >= 760;
  const contextInline = viewportW >= 1100;

  /* PRD F5 — List/Grid, remembered per user. Read in the lazy initializer so
     the first paint is already the right one; written on every pick. */
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    if (typeof window === 'undefined') return 'list';
    try {
      return window.localStorage.getItem(`pear.nb.view.${session()?.memberId ?? 'anon'}`) === 'grid'
        ? 'grid' : 'list';
    } catch { return 'list'; }
  });
  const pickView = useCallback((mode: 'list' | 'grid') => {
    setViewMode(mode);
    try {
      window.localStorage.setItem(`pear.nb.view.${session()?.memberId ?? 'anon'}`, mode);
    } catch { /* private mode — the pick still applies for this visit */ }
  }, []);

  return {
    contextId, setContextId, feedRoving, setFeedRoving,
    viewportW, setViewportW, contextLive, contextInline,
    viewMode, setViewMode, pickView,
  };
}

export type NotebookContext = ReturnType<typeof useNotebookContext>;
